import { loadStripe } from "@stripe/stripe-js";
import api, { isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";

// ——— Stripe.js ———

/** Publishable key for Stripe.js (Checkout redirect). Never put secret keys here. */
export const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

let stripePromise;

/**
 * Lazily loads Stripe.js once per tab. Returns null when the publishable key
 * is missing so callers can fall back to a server-provided checkout URL.
 */
export function getStripe() {
  if (!STRIPE_PUBLISHABLE_KEY) return Promise.resolve(null);
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
}

// ——— Routes (Stripe return URLs) ———

/**
 * Stripe Checkout return URLs for the user portal.
 *
 * Configure your API `success_url`, `cancel_url`, and optional failure redirect
 * to these paths (use `{CHECKOUT_SESSION_ID}` in success_url per Stripe docs).
 */
/** Canonical app routes for payment outcome pages. */
export const INVESTMENT_PAYMENT_ROUTES = {
  success: "/investments/payment/success",
  cancel: "/investments/payment/cancel",
  failed: "/investments/payment/failed",
};

/**
 * Legacy paths used by the Fortify API when creating Stripe Checkout sessions
 * (backend default if it does not apply `success_url` from the checkout request).
 */
export const INVESTMENT_PAYMENT_LEGACY_ROUTES = {
  success: "/investments/success",
  cancel: "/investments/cancel",
  failed: "/investments/failed",
};

/** Stripe success_url template — append to your frontend origin in the API. */
export const STRIPE_CHECKOUT_SUCCESS_URL_TEMPLATE =
  "/investments/payment/success?session_id={CHECKOUT_SESSION_ID}";

export const STRIPE_CHECKOUT_CANCEL_URL_PATH = INVESTMENT_PAYMENT_ROUTES.cancel;

export const STRIPE_CHECKOUT_FAILED_URL_PATH = INVESTMENT_PAYMENT_ROUTES.failed;

const STRIPE_CHECKOUT_SESSION_PLACEHOLDER = "{CHECKOUT_SESSION_ID}";

const DEFAULT_STRIPE_CHECKOUT_PATHS = {
  success: `${INVESTMENT_PAYMENT_LEGACY_ROUTES.success}?session_id=${STRIPE_CHECKOUT_SESSION_PLACEHOLDER}`,
  cancel: INVESTMENT_PAYMENT_LEGACY_ROUTES.cancel,
  failed: INVESTMENT_PAYMENT_LEGACY_ROUTES.failed,
};

/**
 * Origin for Stripe return URLs — uses `window.location.origin` in the browser.
 * `REACT_APP_FRONTEND_ORIGIN` is only a fallback (e.g. non-browser contexts).
 */
export function getStripeFrontendOrigin() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return String(window.location.origin).replace(/\/+$/, "");
  }
  return String(process.env.REACT_APP_FRONTEND_ORIGIN || "")
    .trim()
    .replace(/\/+$/, "");
}

function resolveStripeCheckoutPath(envPath, defaultPath) {
  const trimmed = String(envPath || "").trim();
  if (!trimmed) return defaultPath;
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

/**
 * Full Stripe Checkout return URLs for the API (`origin` + path).
 * Paths default to legacy `/investments/*` routes; override with `REACT_APP_STRIPE_CHECKOUT_*_PATH`.
 */
export function buildStripeCheckoutReturnUrls(origin = getStripeFrontendOrigin()) {
  const successPath = resolveStripeCheckoutPath(
    process.env.REACT_APP_STRIPE_CHECKOUT_SUCCESS_PATH,
    DEFAULT_STRIPE_CHECKOUT_PATHS.success
  );
  const cancelPath = resolveStripeCheckoutPath(
    process.env.REACT_APP_STRIPE_CHECKOUT_CANCEL_PATH,
    DEFAULT_STRIPE_CHECKOUT_PATHS.cancel
  );
  const failurePath = resolveStripeCheckoutPath(
    process.env.REACT_APP_STRIPE_CHECKOUT_FAILURE_PATH,
    DEFAULT_STRIPE_CHECKOUT_PATHS.failed
  );

  return {
    successUrl: buildStripeReturnUrl(successPath, origin),
    cancelUrl: buildStripeReturnUrl(cancelPath, origin),
    failureUrl: buildStripeReturnUrl(failurePath, origin),
  };
}

export function buildStripeReturnUrl(path, origin) {
  const base = (origin || "").replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

/** Reads Stripe `session_id` / legacy aliases from query string. */
export function getStripeSessionId(searchParams) {
  const fromUrl =
    searchParams.get("session_id") || searchParams.get("sessionId");
  if (fromUrl) return fromUrl;
  return null;
}

// ——— Invest page paths ———

/** Return location for sign-in / navigation to the invest confirm page. */
export function buildInvestReturnLocation(projectId, amount, minInvestment, maxInvestment) {
  const params = new URLSearchParams();
  if (Number.isFinite(Number(amount))) {
    params.set("amount", String(amount));
  }
  if (minInvestment) {
    params.set("min", String(minInvestment));
  }
  if (maxInvestment != null && maxInvestment !== "") {
    params.set("max", String(maxInvestment));
  }
  const search = params.toString();
  return {
    pathname: `/invest/${projectId}`,
    search: search ? `?${search}` : "",
  };
}

/** Full path for `navigate()` / `<Link to>` to the invest confirm page. */
export function buildInvestPagePath(projectId, amount, minInvestment, maxInvestment) {
  const { pathname, search } = buildInvestReturnLocation(
    projectId,
    amount,
    minInvestment,
    maxInvestment
  );
  return `${pathname}${search}`;
}

// ——— Investment flow (offering document → risk acknowledgement → invest) ———

export const INVESTMENT_FLOW_ROUTES = {
  riskDisclosure: "/risk-disclosure",
  offeringDocument: "/offering-document",
};

/** Checkbox ids that must be checked on the risk acknowledgement form. */
export const RISK_DISCLOSURE_REQUIRED_ACK_IDS = [
  "risk-q1",
  "risk-q2",
  "risk-q3",
  "risk-q4",
  "risk-q5",
  "risk-q6",
  "risk-q7",
  "risk-q8",
  "risk-q9",
  "risk-b1",
  "risk-b2",
];

function appendInvestmentAmountParams(
  params,
  { amount, minInvestment, maxInvestment } = {}
) {
  if (Number.isFinite(Number(amount))) {
    params.set("amount", String(amount));
  }
  if (minInvestment) {
    params.set("min", String(minInvestment));
  }
  if (maxInvestment != null && maxInvestment !== "") {
    params.set("max", String(maxInvestment));
  }
}

/** Query params shared across risk disclosure, offering document, and invest steps. */
export function buildInvestmentFlowSearchParams(
  campaignId,
  amount,
  minInvestment,
  maxInvestment
) {
  const params = new URLSearchParams();
  if (campaignId) {
    params.set("campaignId", String(campaignId));
  }
  appendInvestmentAmountParams(params, {
    amount,
    minInvestment,
    maxInvestment,
  });
  return params;
}

function parseInvestmentFlowAmount(amountRaw) {
  if (amountRaw == null || amountRaw === "") {
    return Number.NaN;
  }
  const parsed = Number(amountRaw);
  return Number.isNaN(parsed) ? Number.NaN : parsed;
}

export function parseInvestmentFlowSearchParams(searchParams) {
  const campaignId = searchParams.get("campaignId") || "";
  const amountRaw = searchParams.get("amount");
  const amount = parseInvestmentFlowAmount(amountRaw);
  const minRaw = searchParams.get("min");
  const maxRaw = searchParams.get("max");
  const minInvestment =
    minRaw != null && minRaw !== "" ? Number(minRaw) : 0;
  const maxInvestment =
    maxRaw != null && maxRaw !== "" ? Number(maxRaw) : undefined;
  return { campaignId, amount, minInvestment, maxInvestment };
}

export function buildRiskDisclosureLocation(
  campaignId,
  amount,
  minInvestment,
  maxInvestment
) {
  const params = buildInvestmentFlowSearchParams(
    campaignId,
    amount,
    minInvestment,
    maxInvestment
  );
  const search = params.toString();
  return {
    pathname: INVESTMENT_FLOW_ROUTES.riskDisclosure,
    search: search ? `?${search}` : "",
  };
}

export function buildRiskDisclosurePath(
  campaignId,
  amount,
  minInvestment,
  maxInvestment
) {
  const { pathname, search } = buildRiskDisclosureLocation(
    campaignId,
    amount,
    minInvestment,
    maxInvestment
  );
  return `${pathname}${search}`;
}

export function buildOfferingDocumentLocation(
  campaignId,
  amount,
  minInvestment,
  maxInvestment
) {
  const params = buildInvestmentFlowSearchParams(
    campaignId,
    amount,
    minInvestment,
    maxInvestment
  );
  const search = params.toString();
  return {
    pathname: INVESTMENT_FLOW_ROUTES.offeringDocument,
    search: search ? `?${search}` : "",
  };
}

export function buildOfferingDocumentPath(
  campaignId,
  amount,
  minInvestment,
  maxInvestment
) {
  const { pathname, search } = buildOfferingDocumentLocation(
    campaignId,
    amount,
    minInvestment,
    maxInvestment
  );
  return `${pathname}${search}`;
}

export function areAllRiskAcknowledgementsChecked(acknowledged = {}) {
  return RISK_DISCLOSURE_REQUIRED_ACK_IDS.every((id) => Boolean(acknowledged[id]));
}

// ——— Checkout API & session storage ———

export const PENDING_CHECKOUT_STORAGE_KEY = "fortify_pending_checkout";

/** Unwrap `{ data: ... }` envelopes from the API. */
export function unwrapApiPayload(resp) {
  return resp?.data ?? resp ?? {};
}

export function isPaidInvestmentStatus(status) {
  const s = String(status || "").toLowerCase();
  return s === "paid" || s === "completed" || s === "succeeded" || s === "active";
}

export function isFailedInvestmentStatus(status) {
  const s = String(status || "").toLowerCase();
  return (
    s === "failed" ||
    s === "canceled" ||
    s === "cancelled" ||
    s === "unpaid" ||
    s === "expired"
  );
}

function firstDefined(...candidates) {
  for (const candidate of candidates) {
    if (candidate != null && candidate !== "") {
      return candidate;
    }
  }
  return null;
}

/**
 * Normalises checkout creation / sync responses. Backend may return flat keys
 * or nest under `data` (see Postman: investment, stripeCheckoutSessionId).
 */
export function extractCheckoutPayload(resp) {
  const root = unwrapApiPayload(resp);
  const investment = root.investment ?? null;

  return {
    investment,
    investmentId: firstDefined(
      investment?._id,
      investment?.id,
      root.investmentId
    ),
    sessionId: firstDefined(
      root.stripeCheckoutSessionId,
      root.sessionId,
      root.checkoutSessionId
    ),
    checkoutUrl: firstDefined(
      root.checkoutUrl,
      root.url,
      root.stripeCheckoutUrl,
      root.checkout?.url
    ),
    status: firstDefined(investment?.status, root.status),
    message: firstDefined(root.message, resp?.message),
  };
}

function formatCampaignDate(date) {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function inactiveCampaignEligibilityError(normalizedStatus) {
  if (normalizedStatus === "closed") {
    return {
      title: "Campaign ended",
      message:
        "This offering is no longer accepting investments. Browse other opportunities to invest.",
    };
  }
  return {
    title: "Investment unavailable",
    message:
      "Investments are only accepted while this offering is active. Please check back when the campaign opens for investment.",
  };
}

function campaignNotStartedError(campaignStartAt, now) {
  const startMs = new Date(campaignStartAt).getTime();
  if (Number.isNaN(startMs) || now >= startMs) return null;
  const formatted = formatCampaignDate(campaignStartAt);
  return {
    title: "Campaign not started",
    message: formatted
      ? `This offering opens on ${formatted}. Please check back then to invest.`
      : "This offering has not started yet. Please check back later to invest.",
  };
}

function campaignEndedByDateError(campaignEndAt, now) {
  const endMs = new Date(campaignEndAt).getTime();
  if (Number.isNaN(endMs) || now <= endMs) return null;
  const formatted = formatCampaignDate(campaignEndAt);
  return {
    title: "Campaign ended",
    message: formatted
      ? `This offering closed on ${formatted} and is no longer accepting investments.`
      : "This offering has ended and is no longer accepting investments.",
  };
}

function campaignEndedByDaysLeftError(daysRemaining) {
  const days =
    daysRemaining != null && daysRemaining !== "" ? Number(daysRemaining) : null;
  if (days == null || Number.isNaN(days) || days > 0) return null;
  return {
    title: "Campaign ended",
    message:
      "This offering is no longer accepting investments. Browse other opportunities to invest.",
  };
}

/**
 * Returns `{ title, message }` when the campaign is outside its invest window, else `null`.
 * Investments are only allowed when `status` is `active`.
 */
export function validateCampaignInvestEligibility({
  campaignStartAt,
  campaignEndAt,
  daysRemaining,
  status,
  canInvest,
} = {}) {
  const now = Date.now();
  const normalizedStatus = String(status || "").trim().toLowerCase();
  const isInactive =
    canInvest === false || (canInvest !== true && normalizedStatus !== "active");

  if (isInactive) {
    return inactiveCampaignEligibilityError(normalizedStatus);
  }

  if (campaignStartAt) {
    const notStarted = campaignNotStartedError(campaignStartAt, now);
    if (notStarted) return notStarted;
  }

  if (campaignEndAt) {
    const ended = campaignEndedByDateError(campaignEndAt, now);
    if (ended) return ended;
  }

  return campaignEndedByDaysLeftError(daysRemaining);
}

/**
 * Runs all pre-action invest checks (project, campaign window, amount).
 * Returns `{ ok: true }` or `{ ok: false, title, message, severity }`.
 */
export function validateBeforeInvest({
  projectId,
  amount,
  minInvestment = 0,
  maxInvestment,
  campaignStartAt,
  campaignEndAt,
  daysRemaining,
  status,
  canInvest,
} = {}) {
  if (!projectId) {
    return {
      ok: false,
      title: "Invalid campaign",
      message: "This investment opportunity is unavailable.",
      severity: "error",
    };
  }

  const windowError = validateCampaignInvestEligibility({
    campaignStartAt,
    campaignEndAt,
    daysRemaining,
    status,
    canInvest,
  });
  if (windowError) {
    return { ok: false, ...windowError, severity: "warning" };
  }

  const amountError = validateInvestmentAmount(amount, {
    min: minInvestment,
    max: maxInvestment,
  });
  if (amountError) {
    return {
      ok: false,
      title: "Invalid amount",
      message: amountError,
      severity: "warning",
    };
  }

  return { ok: true };
}

export function validateInvestmentAmount(amount, { min = 0, max } = {}) {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) {
    return "Enter a valid investment amount greater than zero.";
  }
  const minVal = Number(min) || 0;
  if (minVal > 0 && value < minVal) {
    return `Minimum investment is $${minVal.toLocaleString("en-US")}.`;
  }
  const maxVal = max != null && max !== "" ? Number(max) : null;
  if (maxVal != null && Number.isFinite(maxVal) && maxVal > 0 && value > maxVal) {
    return `Maximum investment is $${maxVal.toLocaleString("en-US")}.`;
  }
  return null;
}

export function readPendingCheckout() {
  try {
    const raw = sessionStorage.getItem(PENDING_CHECKOUT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Persists checkout session metadata for retry-safe sync without duplicate charges. */
export function savePendingCheckout(payload) {
  sessionStorage.setItem(
    PENDING_CHECKOUT_STORAGE_KEY,
    JSON.stringify({ ...payload, savedAt: Date.now() })
  );
}

export function clearPendingCheckout() {
  sessionStorage.removeItem(PENDING_CHECKOUT_STORAGE_KEY);
}

/**
 * POST /api/investments/:projectId/checkout — creates investment + Stripe session.
 * Sends `success_url`, `cancel_url`, and optional `failure_url` for Stripe redirects.
 */
export async function createInvestmentCheckout(projectId, amount, options = {}) {
  const returnUrls =
    options.returnUrls || buildStripeCheckoutReturnUrls(options.origin);

  const resp = await api.post(API_ENDPOINTS.investmentCheckout(projectId), {
    amount: Number(amount),
    success_url: returnUrls.successUrl,
    cancel_url: returnUrls.cancelUrl,
    failure_url: returnUrls.failureUrl,
    successUrl: returnUrls.successUrl,
    cancelUrl: returnUrls.cancelUrl,
    failureUrl: returnUrls.failureUrl,
  });

  if (!isSuccessResponse(resp)) {
    const message =
      resp?.message || "Could not start checkout. Please try again.";
    const err = new Error(message);
    err.response = resp;
    throw err;
  }

  return extractCheckoutPayload(resp);
}

/**
 * POST /api/investments/sync-session — confirms payment when webhooks lag.
 */
export async function syncInvestmentSession(sessionId) {
  if (!sessionId) {
    throw new Error("Missing checkout session id.");
  }

  const resp = await api.post(API_ENDPOINTS.investmentSyncSession, {
    sessionId,
  });

  if (!isSuccessResponse(resp)) {
    const message =
      resp?.message || "Could not confirm your payment. Please contact support.";
    const err = new Error(message);
    err.response = resp;
    throw err;
  }

  return extractCheckoutPayload(resp);
}

/**
 * Redirects to Stripe Checkout (hosted page). Uses `replace` so the app unloads
 * cleanly and devtools/IDE breakpoints are less likely to fire during navigation.
 * @returns {Promise<boolean>} `true` when a redirect was started
 */
export async function redirectToStripeCheckout({ sessionId, checkoutUrl }) {
  const url = String(checkoutUrl || "").trim();

  if (url) {
    window.location.replace(url);
    return true;
  }

  if (!sessionId) {
    throw new Error(
      "Checkout session was not returned. Please try again or contact support."
    );
  }

  const stripe = await getStripe();
  if (!stripe) {
    throw new Error(
      "Payment is not configured (missing Stripe publishable key). Please contact support."
    );
  }

  const { error } = await stripe.redirectToCheckout({ sessionId });
  if (error) {
    throw error;
  }
  return true;
}
